const parseJsonValue = (value: any): any => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getAnswer = (row: any): any => parseJsonValue(
  row?.answer ?? row?.output?.answer ?? row?.outputs?.[0]?.answer
);

const getTotals = (row: any, answer: any): any => parseJsonValue(
  answer?.totals ?? row?.totals ?? row?.output?.totals
);

const hasCostOutput = (value: any): boolean => {
  const row = parseJsonValue(value);
  if (!row || typeof row !== "object") return false;

  const answer = getAnswer(row);
  const totals = getTotals(row, answer);
  const hasTotals = totals && typeof totals === "object" && [
    "total_production_cost",
    "total_cost",
    "overheads",
    "margins",
  ].some((key) => Object.prototype.hasOwnProperty.call(totals, key));
  const hasDetails = answer && [
    "material_cost",
    "labour_cost",
    "capital_cost",
    "utility_cost",
  ].some((key) => Array.isArray(answer[key]));

  return Boolean(hasTotals || hasDetails);
};

const findCostRows = (result: any): any[] => {
  const rows: any[] = [];
  const visited = new Set<any>();

  const visit = (value: any) => {
    const parsed = parseJsonValue(value);
    if (!parsed || typeof parsed !== "object" || visited.has(parsed)) return;
    visited.add(parsed);

    if (!Array.isArray(parsed) && hasCostOutput(parsed)) {
      rows.push(parsed);
      return;
    }

    if (Array.isArray(parsed) && parsed.some(hasCostOutput)) {
      rows.push(...parsed.filter(hasCostOutput));
      return;
    }

    Object.values(parsed).forEach(visit);
  };

  visit(result);
  return rows;
};

const toAmount = (value: any): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;
  const normalized = value.trim();
  if (!normalized) return 0;
  const negative = /^\(.*\)$/.test(normalized);
  const amount = Number(normalized.replace(/[,$\s()A-Za-z]/g, ""));
  return Number.isFinite(amount) ? (negative ? -amount : amount) : 0;
};

const sumRows = (rows: any, field: string): number =>
  Array.isArray(rows)
    ? rows.reduce((sum, row) => sum + toAmount(row?.[field]), 0)
    : 0;

export interface CostModelHeroTotals {
  production: number;
  overheads: number;
  interest: number;
  margin: number;
  total: number;
  currency: string;
}

export function aggregateCostModelHeroTotals(result: any): CostModelHeroTotals | null {
  const rows = findCostRows(result);
  if (rows.length === 0) return null;

  return rows.reduce<CostModelHeroTotals>((aggregate, rawRow) => {
    const row = parseJsonValue(rawRow);
    const answer = getAnswer(row) ?? {};
    const totals = getTotals(row, answer) ?? {};
    const labourBenefits = sumRows(answer.labour_cost, "benefits_cost") ||
      sumRows(answer.labour_cost, "benefits");
    const detailProduction =
      sumRows(answer.material_cost, "cost") +
      sumRows(answer.labour_cost, "salary") +
      labourBenefits +
      sumRows(answer.capital_cost, "cost") +
      sumRows(answer.utility_cost, "cost");
    const overheads = toAmount(totals.overheads);
    const interest = toAmount(totals.interest_expense) || toAmount(totals.interest_expense_cost);
    const margin = toAmount(totals.margins);
    const production = toAmount(totals.total_production_cost) || detailProduction ||
      Math.max(0, toAmount(totals.total_cost) - overheads - interest - margin);
    const total = toAmount(totals.total_cost) || production + overheads + interest + margin;

    aggregate.production += production;
    aggregate.overheads += overheads;
    aggregate.interest += interest;
    aggregate.margin += margin;
    aggregate.total += total;
    if (!aggregate.currency) {
      aggregate.currency = String(totals.currency ?? answer?.heading?.currency ?? "USD").trim() || "USD";
    }
    return aggregate;
  }, { production: 0, overheads: 0, interest: 0, margin: 0, total: 0, currency: "" });
}